import { type Game, type UseGameAssetParameters, GameAssetTypes, GameTileType, type GameAssetTypesUnion } from '@game/types'
import { useGameConfig, useCoreConfig, createError, useFormat, ref, computed, useMediaAutoplay, useAssetAbTests } from '#imports'

// `fallback` must resolve to a non-empty string. The function form is for
// callers whose fallback comes from a reactive theme ref (resolved fresh
// on each read); callers commit to the brand contract that the slot is
// defined (asserted with `!` at the call site). An empty fallback would
// render `<NuxtImg src="">` — a broken-image placeholder, never desirable.
export function useAssetFallback(getPrimary: () => string | null, fallback: string | (() => string)) {
    const errored = ref(false)
    const resolveFallback = typeof fallback === 'string' ? () => fallback : fallback
    const src = computed(() => errored.value ? resolveFallback() : (getPrimary() ?? resolveFallback()))
    const isFallback = computed(() => src.value === resolveFallback())

    return {
        src,
        isFallback,
        onError: () => { errored.value = true },
        reset: () => { errored.value = false },
    }
}

export const useGameAssets = () => {
    const { assetsUrl, assetsSiteFolder, brandGameAssets } = useGameConfig()
    const { company } = useCoreConfig()
    const { asDatetime } = useFormat()
    const { variantFor } = useAssetAbTests()

    // One tz() per game: a tile builds several asset URLs from one updated_at, and SSR does not cache computeds.
    let stamp: { updatedAt: string, value: string } | undefined

    function timestampOf(updatedAt: string) {
        // Kept as is, second `M` included: cached asset URLs are keyed on it.
        if (stamp?.updatedAt !== updatedAt) stamp = { updatedAt, value: asDatetime(updatedAt, { format: 'YYMDHMms' }) }

        return stamp.value
    }

    // Same gate as the hero videos, so a tile never autoplays where a hero would not.
    const { autoplayAllowed: loadVideo } = useMediaAutoplay()

    function isVideoTile(game: Game | undefined, videoEnabled: boolean) {
        if (!loadVideo.value || !videoEnabled) return false
        if (!hasAsset(game, GameAssetTypes.VIDEO)) return false

        return game?.tile_type === GameTileType.VIDEO
    }

    function isVideoSmallTile(game: Game | undefined, videoEnabled: boolean) {
        if (!loadVideo.value || !videoEnabled) return false
        if (!hasAsset(game, GameAssetTypes.VIDEO_SMALL)) return false

        return game?.tile_type === GameTileType.VIDEO_SMALL
    }

    function getFileExtension(gameAssetType: GameAssetTypesUnion) {
        if (gameAssetType === GameAssetTypes.VIDEO || gameAssetType === GameAssetTypes.VIDEO_SMALL || gameAssetType === GameAssetTypes.VIDEO_TALL || gameAssetType === GameAssetTypes.HERO_VIDEO) {
            return 'mp4'
        }

        if (gameAssetType === GameAssetTypes.HERO_BACKGROUND) {
            return 'jpg'
        }

        return 'png'
    }

    function assetUrl(game: UseGameAssetParameters | Game | undefined | null, type: GameAssetTypesUnion = GameAssetTypes.LOGO, external?: boolean) {
        if (!game) {
            return ' ' // Return string with a space, since nuxt-image will throw an error.
        }

        const { slug, vendor_slug, provider_slug, updated_at } = game

        if (!slug || !vendor_slug || !provider_slug || !updated_at) {
            console.log('useGameAssets - assetsUrl: game missing one of these props: slug, vendor_slug, provider_slug, updated_at')

            return ' '
        }

        if (!company?.websiteName) {
            throw createError({ statusCode: 500, statusMessage: 'useGameAssets: Missing core value: company.websiteName is not set' })
        }

        const assetExtension = getFileExtension(type)
        const timestamp = timestampOf(updated_at)
        let siteFolder = assetsSiteFolder || company.websiteName.toLowerCase()
        // game-configuration resolves the folder per asset for brands with their own folder,
        // falling back to the site folder where the brand has no copy of that asset.
        const brandFolder = game.asset_folders?.[type]

        if (brandFolder) {
            siteFolder = brandFolder
        }
        else if (brandGameAssets) {
            // Check if game has Brand Game Assets
            const { slugs, folderName } = brandGameAssets

            if (slugs && slugs.includes(slug)) {
                siteFolder = folderName
            }
        }

        // A running TileGen A/B test serves this bucket a `<type>__<variant>` file
        // published next to the canonical one, cache-busted by the manifest.
        const variant = variantFor(slug, type, siteFolder)
        const fileName = `${type}${variant?.suffix ?? ''}.${assetExtension}`
        const cacheBust = variant?.t ?? timestamp

        return `${external ? assetsUrl : ''}/game_tiles/${provider_slug}/${vendor_slug}/${slug}/${siteFolder}/${fileName}?t=${cacheBust}`
    }

    function mediaCdnUrl(path: string, width: number, height?: number, audio = true) {
        const options = {
            width: Math.round(width / 2) * 2,
            height: height ? Math.round(height / 2) * 2 : undefined,
            audio: audio ? undefined : 'false',
        }

        const optionsString = Object.entries(options)
            .filter(([_, value]) => value)
            .map(([key, value]) => `${key}=${value}`)
            .join(',')

        return `/cdn-cgi/media/${optionsString}${path}`
    }

    function mediaVideoUrl(src: string | undefined | null, width?: number, height?: number, audio = false) {
        if (!src || src.startsWith('http') || src.includes('/cdn-cgi/media/')) return src ?? undefined
        if (!width) return `${assetsUrl}${src}`
        return `${assetsUrl}${mediaCdnUrl(src, width, height, audio)}`
    }

    function mediaImageUrl(src: string | undefined | null) {
        if (!src || src.startsWith('http') || src.includes('/cdn-cgi/')) return src ?? undefined
        return `${assetsUrl}${src}`
    }

    function videoAssetUrl(game: UseGameAssetParameters | Game | undefined | null, type: GameAssetTypesUnion = GameAssetTypes.VIDEO, width?: number, height?: number, audio = false) {
        const path = assetUrl(game, type)

        if (!path || path === ' ' || !width) {
            return assetUrl(game, type, true)
        }

        return `${assetsUrl}${mediaCdnUrl(path, width, height, audio)}`
    }

    // Strict: only render when the server has explicitly confirmed the asset
    // exists. `null` (server hasn't checked yet) and `undefined` (legacy
    // response without the field) are treated as "no" — generating a URL we
    // can't be sure resolves causes a 404 flash before the fallback kicks in.
    function hasAsset(game: Game | undefined | null, type: GameAssetTypesUnion): boolean {
        return game?.asset_availability?.[type] === true
    }

    function firstAvailable(game: Game | undefined | null, ...types: GameAssetTypesUnion[]): string | null {
        for (const type of types) {
            if (hasAsset(game, type)) return assetUrl(game, type)
        }
        return null
    }

    return {
        assetUrl,
        videoAssetUrl,
        mediaCdnUrl,
        mediaVideoUrl,
        mediaImageUrl,
        loadVideo,
        isVideoTile,
        isVideoSmallTile,
        hasAsset,
        firstAvailable,
    }
}
